// PELUNASAN UNTUK BON TERTENTU (owner 2026-10-30, "Bakso Moujur"): a bon payment may name the bon(s) it
// pays. Its money closes those first; the rest — and every payment without a choice — still pays the
// OLDEST open bon first. Sisa Bon never moves, only WHICH bon stays open (invoice, Umur Piutang).
//   BonTargetPicker    — the optional "Untuk bon" list in every pelunasan form (desktop, HP, lapangan)
//   PaymentTargetModal — "Atur bon yang dibayar" on a recorded pelunasan → approval inbox (like a koreksi)
//   PaymentTargetLines — what such a request changes, in the approval inbox
const { useState: uSbt, useEffect: uEbt } = React;

// The server's own sentence; otherwise what failed (HTTP 502, offline…) — never a misleading generic line.
const btErr = (e) => (e && e.body && e.body.error && e.body.error.message) || trD('common.loadFail') + (e && e.message ? ' — ' + e.message : '');

// The customer's open bons (forPayment: a recorded pelunasan left out). null while loading.
function useOpenBons(customerId, forPayment) {
  const [st, setSt] = uSbt({ lines: null, err: '' });
  uEbt(() => {
    setSt({ lines: null, err: '' });
    if (!customerId) return undefined;
    let live = true;
    window.API.distribusi.openBons(customerId, forPayment)
      .then((r) => { if (live) setSt({ lines: (r && r.data && r.data.lines) || [], err: '' }); })
      .catch((e) => { if (live) setSt({ lines: [], err: btErr(e) }); });
    return () => { live = false; };
  }, [customerId, forPayment]);
  return st;
}

// `value` = picked bon ids, `onChange(ids)`. `amount` (optional) explains where the money goes;
// `onUseSum(n)` offers "Bayar pas" = what the picked bons still owe. `full`: show each bon's FULL value
// (a recorded payment being pointed at the bons it paid), not what is still open of it.
function BonTargetPicker({ customerId, value, onChange, amount, onUseSum, forPayment, lines: given, full }) {
  const own = useOpenBons(given ? null : customerId, forPayment);
  const lines = given || own.lines;
  const picked = new Set(value || []);
  const toggle = (id) => onChange(picked.has(id) ? (value || []).filter((x) => x !== id) : [...(value || []), id]);
  const val = (l) => (full ? l.original : l.remaining);
  const sum = (lines || []).filter((l) => picked.has(l.rowId)).reduce((s, l) => s + val(l), 0);
  const amt = Number(amount) || 0;
  return (
    <div className="bt-pick">
      <div className="bt-pick-h">{trD('dist.btLbl')}</div>
      {own.err ? <div className="bt-pick-note bad">{own.err}</div>
        : lines === null ? <div className="bt-pick-note">{trD('dist.btLoading')}</div>
        : lines.length === 0 ? <div className="bt-pick-note">{trD('dist.btNone')}</div>
        : (
          <div className="bt-pick-list" role="group" aria-label={trD('dist.btLbl')}>
            {lines.map((l) => (
              <label key={l.rowId} className={'bt-pick-row' + (picked.has(l.rowId) ? ' on' : '')}>
                <input type="checkbox" checked={picked.has(l.rowId)} onChange={() => toggle(l.rowId)} />
                <span className="bt-pick-main">
                  <b>{fmtDateShort(l.date)}</b>
                  <span>{l.qty ? trD('dist.btRow', { n: l.qty }) : trD('dist.btKind_' + l.kind)} · {l.ref}</span>
                </span>
                <span className="bt-pick-amt">
                  <b className="tnum">{rpFull(val(l))}</b>
                  {!full && l.remaining < l.original ? <span>{trD('dist.btOf', { rp: rpFull(l.original) })}</span> : null}
                </span>
              </label>
            ))}
          </div>
        )}
      <div className="bt-pick-note">
        {!picked.size ? trD('dist.btHint') : <>
          {trD('dist.btPicked', { rp: rpFull(sum) })}
          {amt > sum ? ' · ' + trD('dist.btOver', { rp: rpFull(amt - sum) }) : amt > 0 && amt < sum ? ' · ' + trD('dist.btShort', { rp: rpFull(sum - amt) }) : ''}
          {onUseSum && sum > 0 && amt !== sum ? <button type="button" className="bt-pick-link" onClick={() => onUseSum(sum)}>{trD('dist.btUseSum', { rp: rpFull(sum) })}</button> : null}
        </>}
      </div>
    </div>
  );
}

// "Atur bon yang dibayar" — point a RECORDED pelunasan at the bon(s) it really paid. Filed under
// distribusiKoreksi, decided in the approval inbox; the server re-checks it and blocks while a bon whose
// open amount would change sits on an issued invoice (delete + re-issue that invoice first).
function PaymentTargetModal({ txn, onClose, onDone }) {
  const [targets, setTargets] = uSbt(() => (txn.bonTargets || []).slice());
  const [reason, setReason] = uSbt('');
  const [prev, setPrev] = uSbt(null);   // { changes, blocks } | { same: true } | { err }
  const [saving, setSaving] = uSbt(false);
  const [err, setErr] = uSbt('');
  const bons = useOpenBons(txn.customerId, txn.id);
  uEbt(() => { const o = (e) => e.key === 'Escape' && onClose(); window.addEventListener('keydown', o); return () => window.removeEventListener('keydown', o); }, []);
  uEbt(() => {
    let live = true;
    setPrev(null);
    const h = setTimeout(() => {
      window.API.distribusi.paymentTarget.preview({ transactionId: txn.id, bonTargets: targets })
        .then((r) => { if (live) setPrev(r.data || { changes: [], blocks: [] }); })
        .catch((e) => { if (!live) return; const m = btErr(e); setPrev(/tidak ada perubahan/i.test(m) ? { same: true } : { err: m }); });
    }, 250);
    return () => { live = false; clearTimeout(h); };
  }, [targets.slice().sort().join(',')]);
  const blocked = !!(prev && prev.blocks && prev.blocks.length);
  const valid = !!(prev && prev.changes && !blocked && reason.trim());
  const submit = () => {
    if (!valid || saving) return;
    setSaving(true); setErr('');
    window.API.distribusi.paymentTarget.submit({ transactionId: txn.id, bonTargets: targets, reason: reason.trim() })
      .then((r) => { setSaving(false); onDone(r.data); })
      .catch((e) => { setSaving(false); setErr(btErr(e)); });
  };
  const cust = txn.customer || {};
  return (
    <div className="modal-scrim" onClick={onClose} style={{ zIndex: 210 }}>
      <div className="modal-card" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>{trD('dist.btTitle')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-mut)', marginTop: 3 }}>{(cust.code ? cust.code + ' · ' : '') + (cust.name || '')}{cust.name ? ' · ' : ''}{trD('dist.btSub', { rp: rpFull(txn.amount), date: fmtDateShort(txn.txnDate) })}</div>
          </div>
          <button className="jp-icon" onClick={onClose} aria-label={trD('common.close') || 'Tutup'}><IconClose s={18} /></button>
        </div>
        <div className="modal-body">
          <BonTargetPicker value={targets} onChange={setTargets} amount={Number(txn.amount)} lines={bons.err ? [] : bons.lines} full />
          {bons.err ? <div className="add-err" style={{ marginTop: 8 }}><IconClose s={14} />{bons.err}</div> : null}
          <div className="bt-effect">
            <div className="bt-pick-h">{trD('dist.btChanges')}</div>
            {prev === null ? <div className="bt-pick-note">{trD('dist.btLoading')}</div>
              : prev.same ? <div className="bt-pick-note">{trD('dist.btNoChange')}</div>
              : prev.err ? <div className="add-err"><IconClose s={14} />{prev.err}</div>
              : <PaymentTargetLines r={prev} />}
            {blocked ? <div className="add-err" style={{ marginTop: 8 }}><IconClose s={14} /><span>{prev.blocks.map((b) => trD('dist.btBlockInv', { date: fmtDateShort(b.date), inv: b.invoice })).join(' · ')}</span></div> : null}
          </div>
          <label className="fld-label">{trD('dist.korekReason')} <span style={{ color: 'var(--neg)' }}>*</span></label>
          <textarea className="fld" style={{ height: 62, padding: 12, resize: 'vertical' }} value={reason} placeholder={trD('dist.btReasonPh')} onChange={(e) => setReason(e.target.value)} />
          <div className="dist-hint">{trD('dist.btNeedsApproval')}</div>
          {err ? <div className="add-err" style={{ marginTop: 8 }}><IconClose s={14} />{err}</div> : null}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>{trD('dist.cancel')}</button>
          <button className="btn btn-primary" disabled={!valid || saving} onClick={submit}>{saving ? '…' : trD('dist.btSubmit')}</button>
        </div>
      </div>
    </div>
  );
}

// What a payment_target request does: "Untuk bon …" then one line per bon whose open amount changes.
function PaymentTargetLines({ r }) {
  const tl = (list) => (list && list.length ? list.map((x) => fmtDateShort(x.date)).join(', ') : trD('dist.btAuto'));
  const val = (n) => (n > 0 ? rpFull(n) : trD('dist.btLunas'));
  return (
    <div className="bt-lines">
      {r.newTargets ? <div className="bt-lines-to">{r.oldTargets ? <><s>{tl(r.oldTargets)}</s> → </> : null}<b>{tl(r.newTargets)}</b></div> : null}
      {(r.changes || []).map((c) => (
        <div key={c.rowId} className="bt-lines-row">
          <span>{trD('dist.btBonOf', { date: fmtDateShort(c.date) })}</span>
          <span className="tnum"><s>{val(c.before)}</s> → <b className={c.after > 0 ? 'amt-neg' : 'amt-pos'}>{val(c.after)}</b></span>
        </div>
      ))}
    </div>
  );
}
